import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "../../components/ui/breadcrumb";

// Valid routes to show in breadcrumbs
const validRoutes = [
  "/",
  "/discover",
  "/my-recipes",
  "/favorites",
  "/plans",
  "/shopping-list",
  "/profile",
  "/settings",
  "/help",
  "/pricing",
  "/admin",
];

// Format breadcrumb labels
const formatLabel = (path: string): string => {
  return path
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export function Breadcrumbs({
  className,
  ...props
}: { className?: string } & React.HTMLAttributes<HTMLElement>) {
  const location = useLocation();
  const pathnames = location.pathname.split("/").filter((x) => x);

  // Don't show breadcrumbs for invalid/loading routes
  if (
    location.pathname === "/loading" ||
    location.pathname.includes("loading")
  ) {
    return null;
  }

  return (
    <Breadcrumb className={className} {...props}>
      <BreadcrumbList className="flex h-9 items-center gap-1.5 text-sm leading-none">
        <BreadcrumbItem className="inline-flex items-center">
          <BreadcrumbLink
            asChild
            className="inline-flex h-9 items-center gap-1.5 align-middle text-martinique/80 hover:text-martinique"
          >
            <Link to="/">
              <Home className="size-4" aria-hidden="true" />
              Home
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        {pathnames.map((name, index) => {
          const routeTo = `/${pathnames.slice(0, index + 1).join("/")}`;
          const isLast = index === pathnames.length - 1;
          const displayName = formatLabel(name);

          return (
            <React.Fragment key={name}>
              <BreadcrumbSeparator className="px-1.5" />
              <BreadcrumbItem className="inline-flex items-center">
                {isLast ? (
                  <BreadcrumbPage className="inline-flex h-9 items-center font-semibold text-martinique">
                    {displayName}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink
                    asChild
                    className="inline-flex h-9 items-center gap-1.5 align-middle text-martinique/80 hover:text-martinique"
                  >
                    <Link to={routeTo}>{displayName}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
